import { Dispatch } from 'react-redux';
import { AxiosError } from "axios";
import { AuthActionCreators, IApplicationState } from "../";
import { IDonorSearchResult, IDashSearchResult, IClosedLoopAssignRequest, IBaseResponse, IFindDonorResult, IClosedLoopCreateLinks, IListItem, IFindDsdResult } from "@nkr/models/index";
import { ClosedLoopAssignType, OrganType } from '@nkr/enums/index';
import { YesNoUnknown } from '@nkr/enums/yes-no-unknown';

export const ActionTypes = {
    SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
    UPDATE_SEARCH_RESULTS: 'UPDATE_SEARCH_RESULTS',
    UPDATE_MARK_DONATED: 'UPDATE_MARK_DONATED'
}

export interface IDonorSearchResults {
    dashResults: IDonorSearchResult[],
    donorResults: IDonorSearchResult[],
    donorShieldDirectResults: IDonorSearchResult[],
    followUpDonorResults: IDonorSearchResult[]
}

export interface IDonorSearchResponse {
    data: {
        success: boolean,
        message: string,
        results: IDonorSearchResults
    }
}

export interface ICreateResponse {
    data: {
        success: boolean,
        messages: string[],
        results: IDonorSearchResult
    }
}

export interface IFindDashResponse {
    data: {
        success: boolean,
        message: string,
        results: IDashSearchResult[]
    }
}

export interface IFindDonorResponse {
    data: {
        success: boolean,
        message: string,
        results: IFindDonorResult[]
    }
}

export interface IFindDsdResponse {
    data: {
        success: boolean,
        message: string,
        results: IFindDsdResult[]
    }
}

export interface IExistsByAliasResponse {
    data: {
        success: boolean,
        results: number
    }
}

export interface ILookupDataResponse {
    data: {
        success: boolean,
        results: {
            closedLoopManualConfirmTypes: IListItem[],
            donationTypes: IListItem[],
            allowVoucherSelfRegistration: boolean
        }
    }
}

export const ActionCreators = {
    doSearch: (centerId: number, firstName: string, lastName: string, alias: string, email: string, dateOfBirth: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorSearchResponse> => {
        var options = {
            url: `${__API__}/donor-search/search`,
            data: {
                centerId,
                firstName,
                lastName,
                alias,
                email,
                dateOfBirth
            }
        };

        try {
            let response = await dispatch(AuthActionCreators.apiPost(options));

            if (response.data.success) {
                dispatch(ActionCreators.setSearchResults(response.data.results));
            }

            return response;
        } catch (error) {
            throw error;
        }
    },
    setSearchResults: (results: IDonorSearchResults) => {
        return {
            type: ActionTypes.SET_SEARCH_RESULTS,
            results
        };
    },
    doCreate: (
        centerId: number, identifier: string,
        registrationId: number, donorId: number, donorShieldDirectId: number, watrRequestId: number, followUpDonorId: number,
        links: IClosedLoopCreateLinks,
        firstName: string, lastName: string,
        address: string, city: string, state: string, postalCode: string,
        phone: string, email: string,
        birthDate: string, registrationDate: string, donationDate: string,
        donationType: number,
        alias: string,
        hasNutcracker: YesNoUnknown,
        sendInvite: boolean,
        medicalRecordId: number
    ) => (dispatch: Dispatch<IApplicationState>): Promise<ICreateResponse> => {
        let options = {
            url: `${__API__}/donor-search/closed-loop-create`,
            data: {
                centerId,
                registrationId,
                donorId,
                donorShieldDirectId,
                watrRequestId,
                followUpDonorId,
                links,
                firstName, lastName,
                address, city, state, postalCode,
                phone, email,
                birthDate,
                registrationDate,
                donationDate,
                donationType,
                alias,
                hasNutcracker,
                sendInvite,
                medicalRecordId
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: ICreateResponse) => {
                if (response.data.success)
                    dispatch(ActionCreators.updateSearchResults(identifier, response.data.results));

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    updateSearchResults: (identifier: string, results: IDonorSearchResult) => {
        return {
            type: ActionTypes.UPDATE_SEARCH_RESULTS,
            identifier,
            results
        };
    },
    doFindDash: (centerId: number, search: string) => (dispatch: Dispatch<IApplicationState>): Promise<IFindDashResponse> => {
        var options = {
            url: `${__API__}/donor-intake/find`,
            data: {
                centerId,
                search
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IFindDashResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    doFindDonor: (centerId: number, search: string) => (dispatch: Dispatch<IApplicationState>): Promise<IFindDonorResponse> => {
        var options = {
            url: `${__API__}/donor/find`,
            data: {
                centerId,
                search
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IFindDonorResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    searchByPerson: (centerId: number, firstName: string, lastName: string, birthDate: string) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        const params = [
            `centerId=${centerId}`,
            `firstName=${encodeURIComponent(firstName || '')}`,
            `lastName=${encodeURIComponent(lastName || '')}`,
            `birthDate=${encodeURIComponent(birthDate || '')}`
        ].join('&');
        return dispatch(AuthActionCreators.apiGet({
            url: `${__API__}/donor/person-search?${params}`
        }));
    },
    doFindDsd: (centerId: number, search: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IFindDsdResponse> => {
        var options = {
            url: `${__API__}/donor-shield-direct/find`,
            data: {
                centerId,
                search
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    doAssign: (item: IDonorSearchResult, type: ClosedLoopAssignType) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var data: IClosedLoopAssignRequest = {
            type,
            registrationId: item.registrationId,
            donorId: item.donorId,
            donorShieldDirectId: item.donorShieldDirectId,
            watrRequestId: item.watrRequestId,
            followUpDonorId: item.followUpDonorId
        };

        var options = {
            url: `${__API__}/donor-search/closed-loop-assign`,
            data
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    existsByAlias: (centerId: number, alias: string) => (dispatch: Dispatch<IApplicationState>): Promise<IExistsByAliasResponse> => {
        var options = {
            url: `${__API__}/donor/existing-by-alias?centerId=${centerId}&alias=${alias}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IExistsByAliasResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    markDonated: (identifier: string, dashRegistrationId: number, donationDate: string, donationType: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options = {
            url: `${__API__}/donor-search/mark-donated`,
            data: {
                dashRegistrationId,
                donationDate,
                donationType
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IDonorSearchResponse) => {
                if (response.data.success) {
                    dispatch(ActionCreators.updateMarkDonated(identifier, 'Manual Confirmation', donationDate));
                }

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    updateMarkDonated: (identifier: string, closeLoopEntityType: string, donationDate: string) => {
        return {
            type: ActionTypes.UPDATE_MARK_DONATED,
            identifier,
            closeLoopEntityType,
            donationDate
        };
    },

    getLookupData: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<ILookupDataResponse> => {
        var options = {
            url: `${__API__}/donor-search/lookup-data?centerId=${centerId}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: ILookupDataResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    }
}

export interface IDonorSearchState {
    searchResults: IDonorSearchResults
}

var initialState: IDonorSearchState = {
    searchResults: {
        dashResults: [],
        donorResults: [],
        donorShieldDirectResults: [],
        followUpDonorResults: []
    },
};

interface ISetSearchResults {
    type: string,
    results: IDonorSearchResults
}

interface IUpdateSearchResults {
    type: string,
    identifier: string,
    results: IDonorSearchResult
}

interface IUpdateMarkDonated {
    type: string,
    identifier: string,
    closeLoopEntityType: string,
    donationDate: string
}

const setSearchResults = (state: IDonorSearchState, action: ISetSearchResults) => {
    state.searchResults = action.results;
    return state;
}

const updateSearchResults = (state: IDonorSearchState, action: IUpdateSearchResults) => {
    let dashResults = state.searchResults.dashResults.map(item => {
        if (item.identifier == action.identifier) {
            item.registrationId = action.results.registrationId;
            item.donorId = action.results.donorId;
            item.donorShieldDirectId = action.results.donorShieldDirectId;
            item.watrRequestId = action.results.watrRequestId;
            item.followUpDonorId = action.results.followUpDonorId;
        };

        return item;
    });

    let donorResults = state.searchResults.donorResults.map(item => {
        if (item.identifier == action.identifier) {
            item.registrationId = action.results.registrationId;
            item.donorId = action.results.donorId;
            item.donorShieldDirectId = action.results.donorShieldDirectId;
            item.watrRequestId = action.results.watrRequestId;
            item.followUpDonorId = action.results.followUpDonorId;
        };

        return item;
    });

    let donorShieldDirectResults = state.searchResults.donorShieldDirectResults.map(item => {
        if (item.identifier == action.identifier) {
            item.registrationId = action.results.registrationId;
            item.donorId = action.results.donorId;
            item.donorShieldDirectId = action.results.donorShieldDirectId;
            item.watrRequestId = action.results.watrRequestId;
            item.followUpDonorId = action.results.followUpDonorId;
        };

        return item;
    });

    let followUpDonorResults = state.searchResults.followUpDonorResults.map(item => {
        if (item.identifier == action.identifier) {
            item.registrationId = action.results.registrationId;
            item.donorId = action.results.donorId;
            item.donorShieldDirectId = action.results.donorShieldDirectId;
            item.watrRequestId = action.results.watrRequestId;
            item.followUpDonorId = action.results.followUpDonorId;
        };

        return item;
    });

    state.searchResults = {
        dashResults,
        donorResults,
        donorShieldDirectResults,
        followUpDonorResults
    };

    return state;
}

const updateMarkDonated = (state: IDonorSearchState, action: IUpdateMarkDonated) => {
    let dashResults = state.searchResults.dashResults.map(item => {
        if (item.identifier == action.identifier) {
            item.closeLoopEntityType = action.closeLoopEntityType;
            item.closeLoopSurgeryDateTime = action.donationDate;
        };

        return item;
    });

    let donorResults = state.searchResults.donorResults.map(item => {
        if (item.identifier == action.identifier) {
            item.closeLoopEntityType = action.closeLoopEntityType;
            item.closeLoopSurgeryDateTime = action.donationDate;
        };

        return item;
    });

    let donorShieldDirectResults = state.searchResults.donorShieldDirectResults.map(item => {
        if (item.identifier == action.identifier) {
            item.closeLoopEntityType = action.closeLoopEntityType;
            item.closeLoopSurgeryDateTime = action.donationDate;
        };

        return item;
    });

    let followUpDonorResults = state.searchResults.followUpDonorResults.map(item => {
        if (item.identifier == action.identifier) {
            item.closeLoopEntityType = action.closeLoopEntityType;
            item.closeLoopSurgeryDateTime = action.donationDate;
        };

        return item;
    });

    state.searchResults = {
        dashResults,
        donorResults,
        donorShieldDirectResults,
        followUpDonorResults
    };

    return state;
}

interface IDonorSearchHandlers {
    [x: string]:
    ((state: IDonorSearchState, action: ISetSearchResults) => IDonorSearchState)
    | ((state: IDonorSearchState, action: IUpdateSearchResults) => IDonorSearchState)
    | ((state: IDonorSearchState, action: IUpdateMarkDonated) => IDonorSearchState)
}

const createReducer = (initialState: IDonorSearchState, handlers: IDonorSearchHandlers) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = Object.assign({}, state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
    [ActionTypes.SET_SEARCH_RESULTS]: setSearchResults,
    [ActionTypes.UPDATE_SEARCH_RESULTS]: updateSearchResults,
    [ActionTypes.UPDATE_MARK_DONATED]: updateMarkDonated,
};

export const Reducer = createReducer(initialState, handlers);
